import { useContext, useState, useMemo, useEffect } from "react"
import { UserContext } from "../Context/UserContext"
import ColorLabels from "./ColorLabels"
import Glossary from "./Glossary"
import { useNavigate } from "react-router-dom"
import "../styles/hitterRatingsTable.css"
import { useIsMobile } from "../hooks/useIsMobile"
import MobileHitterList from "./MobileHitterList"
import HitterRatingsTable from "./HitterRatingsTable"
import HitterFilters from "./HitterFilters"

const EMPTY_RANGE = { min: "" }

const DEFAULT_FILTERS = {
  ratingTier: "all",
  team: "All",
  arsenalTier: "all",
  stats: {
    // Hitter
    hit_pa:                   { ...EMPTY_RANGE },
    hit_wrc_plus:             { ...EMPTY_RANGE },
    hit_woba:                 { ...EMPTY_RANGE },
    hit_matchup_woba:         { ...EMPTY_RANGE },
    hit_iso:                  { ...EMPTY_RANGE },
    hit_matchup_iso:          { ...EMPTY_RANGE },
    hit_hard_contact_pct:     { ...EMPTY_RANGE },
    hit_matchup_hard_contact: { ...EMPTY_RANGE },
    hit_hr_per_fb:            { ...EMPTY_RANGE },
    hit_matchup_hr_per_fb:    { ...EMPTY_RANGE },
    hit_fb_pct:               { ...EMPTY_RANGE },
    hit_matchup_fb_pct:       { ...EMPTY_RANGE },
    // Pitcher
    sp_ip:                    { ...EMPTY_RANGE },
    sp_woba:                  { ...EMPTY_RANGE },
    sp_iso:                   { ...EMPTY_RANGE },
    sp_hard_contact:          { ...EMPTY_RANGE },
    sp_soft_contact:          { ...EMPTY_RANGE },
    sp_kpct:                  { ...EMPTY_RANGE },
    sp_bbpct:                 { ...EMPTY_RANGE },
    sp_xfip:                  { ...EMPTY_RANGE },
    sp_fbpct:                 { ...EMPTY_RANGE },
    sp_gbpct:                 { ...EMPTY_RANGE },
    sp_hr_per_fb:             { ...EMPTY_RANGE },
  },
}

function parseNum(val) {
  const n = parseFloat(val)
  return isNaN(n) ? null : n
}

function applyFilters(data, filters, searchTerm) {
  return data.filter((stat) => {
    // Matchup rating tier — mirrors matchupRatingStyles thresholds exactly
    const r = stat.hit_matchup_rating
    if (filters.ratingTier === "elite"     && r < 0.450)                      return false
    if (filters.ratingTier === "strong"    && !(r >= 0.400 && r < 0.450))     return false
    if (filters.ratingTier === "favorable" && !(r > 0.333 && r < 0.400))      return false
    if (filters.ratingTier === "average"   && !(r > 0.230 && r <= 0.333))     return false
    if (filters.ratingTier === "poor"      && r > 0.230)                      return false

    // Arsenal coverage — minimum star rating
    if (filters.arsenalTier !== "all" && (stat.pitch_overall_stars ?? 0) < Number(filters.arsenalTier)) return false

    // Hitter team
    if (filters.team !== "All" && stat.hitter_team !== filters.team) return false


    // Stat threshold filters (min only)
    for (const [field, { min }] of Object.entries(filters.stats)) {
      const minNum = parseNum(min)
      if (minNum !== null && parseFloat(stat[field]) < minNum) return false
    }

    // Search term
    if (searchTerm && !stat.hitter_name.toLowerCase().includes(searchTerm.toLowerCase())) return false

    return true
  })
}

function countActiveFilters(filters) {
  let count = 0
  if (filters.ratingTier !== "all") count++
  if (filters.arsenalTier !== "all") count++
  if (filters.team !== "All") count++
  for (const { min } of Object.values(filters.stats)) {
    if (min !== "") count++
  }
  return count
}

function HitterRatingsTableContainer({ ratingTimeframe, isActiveSub }) {
  const navigate = useNavigate()
  const { state, dispatch } = useContext(UserContext)
  const [dataTimeframe, setDataTimeframe] = useState("ThreeYear")
  const [sortDirection, setSortDirection] = useState(false)
  const [sortState, setSortState] = useState("")
  const [filters, setFilters] = useState(DEFAULT_FILTERS)
  const [searchTerm, setSearchTerm] = useState("")
  const isMobile = useIsMobile(768)
  const [showFullTable, setShowFullTable] = useState(false)
  const [showFullTablePrompt, setShowFullTablePrompt] = useState(true)

  useEffect(() => {
    setDataTimeframe(ratingTimeframe ? "CurrentSeason" : "ThreeYear")
  }, [ratingTimeframe])

  const rawData = useMemo(() => {
    const key = `daily${dataTimeframe}Stats`
    return Array.isArray(state[key]) ? state[key] : []
  }, [state, dataTimeframe])

  const availableTeams = useMemo(() => {
    const teams = [...new Set(rawData.map((s) => s.hitter_team).filter(Boolean))]
    return teams.sort()
  }, [rawData])

  const filteredAndSorted = useMemo(() => {
    const filtered = applyFilters(rawData, filters, searchTerm)
    return [...filtered].sort((a, b) => {
      const aValue = a?.[sortState] ?? ""
      const bValue = b?.[sortState] ?? ""
      if (typeof aValue === "string" && typeof bValue === "string") {
        return sortDirection ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue)
      }
      const numA = Number(aValue) || 0
      const numB = Number(bValue) || 0
      return sortDirection ? numA - numB : numB - numA
    })
  }, [rawData, filters, searchTerm, sortState, sortDirection])

  const activeFilterCount = useMemo(() => countActiveFilters(filters), [filters])

  function fetchSingleHitterData(hitterID) {
    fetch(`/hitters/${hitterID}`)
      .then((res) => res.json())
      .then((data) => {
        if (data) dispatch({ type: "fetchSingleHitter", payload: data })
        else console.error("Error: Hitter data not found")
      })
  }

  function navigateToHitterPage(hitterName, hitterID) {
    state.singleHitter = []
    const formattedName = hitterName.replace(/ /g, "-")
    fetchSingleHitterData(hitterID)
    navigate(`/hitter/${formattedName}`)
    window.scrollTo(0, 0)
  }

  const sharedProps = {
    sortedHitters: filteredAndSorted,
    data: filteredAndSorted,       // MobileHitterList expects "data"
    dataTimeframe,
    searchTerm,
    onSearchChange: setSearchTerm,
    isActiveSub,
    navigateToHitterPage,
    sortState,
    setSortState,
    setSortDirection,
    // filter props — HitterFilters renders inside the table/list components
    filters,
    setFilters,
    availableTeams,
    activeFilterCount,
  }

  //console.log('filteredAndSorted', filteredAndSorted)

  return (
    <div>

      {isMobile ? (
        <div>
          <div className="mobileContainer">
            {!showFullTablePrompt && <h3 className="changeViewHeader">Change View</h3>}
            <div className="promptBtnContainer">
              <button onClick={() => setShowFullTablePrompt((prev) => !prev)}>
                {showFullTablePrompt ? "-" : "+"}
              </button>
            </div>
            {showFullTablePrompt && (
              <div className="promptHeaderContainer">
                <h2>{showFullTable ? "Go Back to Mobile List" : "Prefer the full table?"}</h2>
                <button onClick={() => setShowFullTable((prev) => !prev)}>
                  {showFullTable ? "View Mobile List" : "Show Full Table"}
                </button>
              </div>
            )}
          </div>
          {showFullTable ? (
            <HitterRatingsTable {...sharedProps} />
          ) : (
            <MobileHitterList {...sharedProps} />
          )}
        </div>
      ) : (
        <HitterRatingsTable {...sharedProps} />
      )}
    </div>
  )
}

export default HitterRatingsTableContainer